export function getRawUnits(item) {
  return Array.isArray(item?.rawUnits) ? item.rawUnits : [];
}

export function getAvailabilitySummary(item) {
  const units = getRawUnits(item);
  const availableCount = units.filter((unit) => Number(unit?.statusCd) === 1000).length;
  const unavailableCount = Math.max(units.length - availableCount, 0);
  const isAvailable = availableCount > 0;

  return {
    units,
    isAvailable,
    availableCount,
    unavailableCount,
    totalUnits: units.length
  };
}

export function getPrimaryUnit(item) {
  const units = getRawUnits(item);
  if (units.length === 0) {
    return null;
  }

  const unitsWithSuggested = units.filter((unit) => Number.isFinite(Number(unit?.suggestedAmt)));
  if (unitsWithSuggested.length === 0) {
    return units[0];
  }

  return unitsWithSuggested.reduce((cheapestUnit, currentUnit) => {
    const cheapestSuggested = Number(cheapestUnit?.suggestedAmt);
    const currentSuggested = Number(currentUnit?.suggestedAmt);
    return currentSuggested < cheapestSuggested ? currentUnit : cheapestUnit;
  });
}

export function formatCurrency(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return null;
  }
  return `$${numeric.toFixed(2)}`;
}

export function formatAgeFromMonths(value) {
  const totalMonths = Math.floor(Number(value));
  if (!Number.isFinite(totalMonths) || totalMonths < 0) {
    return '—';
  }

  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;

  if (years <= 0 && months <= 0) {
    return 'Less than 1 month';
  }

  let yearLabel = null;
  let monthLabel = null;

  if (years > 0) {
    yearLabel = `${years} year${years === 1 ? '' : 's'}`;
  }

  if (months > 0) {
    monthLabel = `${months} month${months === 1 ? '' : 's'}`;
  }

  return [yearLabel, monthLabel].filter(Boolean).join(', ');
}

export function getUnitKey(unit) {
  return [
    unit?.unitNumber || 'unknown',
    unit?.dateAdded || 'na',
    unit?.age || 'na',
    unit?.statusCd || 'na'
  ].join('-');
}

export function parsePositiveAmount(value) {
  if (value === null || value === undefined) {
    return null;
  }
  const numeric = typeof value === 'string'
    ? Number(value.replaceAll('$', '').replaceAll(',', ''))
    : Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
}

function getNowAndWasPrice(suggestedAmount, retailAmount) {
  return {
    nowPrice: suggestedAmount ?? retailAmount,
    wasPrice: retailAmount ?? suggestedAmount
  };
}

function getPromotion(wasPrice, nowPrice) {
  if (wasPrice <= nowPrice) {
    return undefined;
  }

  const savingsValue = wasPrice - nowPrice;
  const savingsPercent = Math.round((savingsValue / wasPrice) * 100);

  return {
    dollarOff: savingsValue,
    percentageOff: savingsPercent
  };
}

function createMappedProduct(resolvedItemId, wasPrice, nowPrice) {
  return {
    identifiers: {
      itemId: resolvedItemId
    },
    pricing: {
      itemId: resolvedItemId,
      original: wasPrice,
      value: nowPrice,
      unitOfMeasure: 'each',
      promotion: getPromotion(wasPrice, nowPrice)
    }
  };
}

export function buildUsedEquipmentPriceModel(item, itemId) {
  const primaryUnit = getPrimaryUnit(item);
  const suggestedAmount = parsePositiveAmount(item?.price ?? primaryUnit?.suggestedAmt);
  const retailAmount = parsePositiveAmount(item?.retailPrice ?? primaryUnit?.retailAmt);

  const resolvedItemId = String(itemId || item?.StrNbr || '000000000');
  const { nowPrice, wasPrice } = getNowAndWasPrice(suggestedAmount, retailAmount);

  if (!nowPrice) {
    return {
      mappedItemId: resolvedItemId,
      mappedProduct: null
    };
  }

  const mappedProduct = createMappedProduct(resolvedItemId, wasPrice, nowPrice);

  return {
    mappedItemId: resolvedItemId,
    mappedProduct
  };
}
